import { useState, useCallback } from "react"
import { useQuery, useMutation } from "urql"
import { CheckCircle2, XCircle, Plus, Trash2, ShieldCheck, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { ProvidersQuery } from "@/lib/graphql/queries"
import { TestWebhookVerificationMutation } from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

interface KeyValuePair {
  id: string
  key: string
  value: string
}

type VerificationResult = ResultOf<typeof TestWebhookVerificationMutation>["testWebhookVerification"]

let nextId = 0
function uniqueId(): string {
  nextId += 1
  return `kv-${nextId}-${Date.now()}`
}

function createPair(key = "", value = ""): KeyValuePair {
  return { id: uniqueId(), key, value }
}

function KeyValueEditor({
  label,
  description,
  pairs,
  onPairsChange,
  keyPlaceholder = "Key",
  valuePlaceholder = "Value",
}: {
  label: string
  description?: string
  pairs: KeyValuePair[]
  onPairsChange: (pairs: KeyValuePair[]) => void
  keyPlaceholder?: string
  valuePlaceholder?: string
}) {
  const handleAdd = () => {
    onPairsChange([...pairs, createPair()])
  }

  const handleRemove = (id: string) => {
    onPairsChange(pairs.filter((p) => p.id !== id))
  }

  const handleChange = (id: string, field: "key" | "value", val: string) => {
    onPairsChange(pairs.map((p) => (p.id === id ? { ...p, [field]: val } : p)))
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <Label>{label}</Label>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>
      {pairs.length === 0 && (
        <p className="text-xs text-muted-foreground italic py-2">No entries. Click Add to begin.</p>
      )}
      <div className="space-y-2">
        {pairs.map((pair) => (
          <div key={pair.id} className="flex items-center gap-2">
            <Input
              placeholder={keyPlaceholder}
              value={pair.key}
              onChange={(e) => handleChange(pair.id, "key", e.target.value)}
              className="flex-1 font-mono text-sm"
            />
            <Input
              placeholder={valuePlaceholder}
              value={pair.value}
              onChange={(e) => handleChange(pair.id, "value", e.target.value)}
              className="flex-1 font-mono text-sm"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => handleRemove(pair.id)}
              aria-label="Remove"
            >
              <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

export function VerificationTestPage() {
  const [providersResult] = useQuery({ query: ProvidersQuery })
  const [, testVerification] = useMutation(TestWebhookVerificationMutation)

  const [provider, setProvider] = useState("")
  const [headers, setHeaders] = useState<KeyValuePair[]>([createPair()])
  const [body, setBody] = useState("")
  const [config, setConfig] = useState<KeyValuePair[]>([createPair("signing_secret", "")])
  const [result, setResult] = useState<VerificationResult | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const providers = providersResult.data?.providers ?? []

  const pairsToRecord = useCallback((pairs: KeyValuePair[]): Record<string, unknown> => {
    const record: Record<string, unknown> = {}
    for (const pair of pairs) {
      if (pair.key.trim()) {
        record[pair.key.trim()] = pair.value
      }
    }
    return record
  }, [])

  const handleVerify = async () => {
    if (!provider) {
      toast.error("Select a provider")
      return
    }
    if (!body.trim()) {
      toast.error("Body is required")
      return
    }

    setSubmitting(true)
    setResult(null)

    const res = await testVerification({
      input: {
        provider,
        headers: pairsToRecord(headers),
        body,
        config: pairsToRecord(config),
      },
    })

    setSubmitting(false)

    if (res.error) {
      toast.error(res.error.message)
      return
    }

    if (res.data) {
      setResult(res.data.testWebhookVerification)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Verification Test"
        description="Test webhook signature verification against any registered provider"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Request</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger id="provider" className="w-full">
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <KeyValueEditor
              label="Headers"
              description="HTTP headers from the webhook request"
              pairs={headers}
              onPairsChange={setHeaders}
              keyPlaceholder="Header name"
              valuePlaceholder="Header value"
            />

            <div className="space-y-2">
              <Label htmlFor="body">Body</Label>
              <Textarea
                id="body"
                placeholder="Raw webhook request body..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="font-mono text-sm min-h-48"
              />
            </div>

            <KeyValueEditor
              label="Config"
              description="Provider configuration (e.g. signing_secret)"
              pairs={config}
              onPairsChange={setConfig}
              keyPlaceholder="Config key"
              valuePlaceholder="Config value"
            />

            <Button
              onClick={handleVerify}
              disabled={submitting}
              className="w-full shadow-primary-glow transition-shadow"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4 mr-1.5" />
              )}
              {submitting ? "Verifying..." : "Verify"}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Result</CardTitle>
          </CardHeader>
          <CardContent>
            {!result && !submitting && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ShieldCheck className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  Submit a verification request to see results
                </p>
              </div>
            )}

            {submitting && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Loader2 className="h-10 w-10 text-muted-foreground/50 mb-3 animate-spin" />
                <p className="text-sm text-muted-foreground">Verifying...</p>
              </div>
            )}

            {result && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  {result.success ? (
                    <Badge
                      variant="outline"
                      className="bg-emerald-500/15 text-emerald-400 border-emerald-500/25 gap-1.5 text-sm py-1 px-2.5"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-rose-500/15 text-rose-400 border-rose-500/25 gap-1.5 text-sm py-1 px-2.5"
                    >
                      <XCircle className="h-4 w-4" />
                      Failed
                    </Badge>
                  )}
                </div>

                {result.error && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Error</Label>
                    <pre className="text-sm text-destructive bg-destructive/10 rounded-lg p-3 whitespace-pre-wrap break-words font-mono">
                      {result.error}
                    </pre>
                  </div>
                )}

                {result.eventType && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Event Type</Label>
                    <p className="text-sm font-mono bg-muted/50 rounded-lg p-3">
                      {result.eventType}
                    </p>
                  </div>
                )}

                {result.idempotencyKey && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Idempotency Key</Label>
                    <p className="text-sm font-mono bg-muted/50 rounded-lg p-3">
                      {result.idempotencyKey}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
